import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';

export function Settings() {
  const { user, logout, refresh } = useAuth();

  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!displayName.trim()) return;
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await api.updateProfile({ display_name: displayName.trim() });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update profile.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Manage your account and profile settings
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {saved && (
        <div className="mb-6 rounded-lg border border-[rgba(52,211,153,0.2)] bg-[rgba(52,211,153,0.12)] px-4 py-3 text-sm text-[var(--color-success)]">
          Profile updated successfully.
        </div>
      )}

      {/* Profile section */}
      <div className="rounded-xl border border-border-dark bg-surface-1 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Profile</h2>

        <div className="mt-6 flex items-start gap-6">
          {/* Avatar */}
          <div className="shrink-0">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="h-16 w-16 rounded-full"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-violet/20 text-xl font-semibold text-accent-violet">
                {user?.display_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>

          {/* Fields */}
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary">
                Email
              </label>
              <p className="mt-1 text-sm text-text-secondary">{user?.email}</p>
            </div>

            <div>
              <label
                htmlFor="settingsDisplayName"
                className="block text-sm font-medium text-text-primary"
              >
                Display name
              </label>
              <input
                id="settingsDisplayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 w-full max-w-sm rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !displayName.trim()}
              className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Linked providers */}
      <div className="mt-6 rounded-xl border border-border-dark bg-surface-1 p-6">
        <h2 className="text-lg font-semibold text-text-primary">
          Linked Providers
        </h2>
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border-dark px-4 py-3">
            <div className="flex items-center gap-3">
              <svg className="h-5 w-5 text-text-primary" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span className="text-sm font-medium text-text-primary">Google</span>
            </div>
            <span
              className={`text-xs font-medium ${
                user?.provider === 'google'
                  ? 'text-[var(--color-success)]'
                  : 'text-text-tertiary'
              }`}
            >
              {user?.provider === 'google' ? 'Connected' : 'Not connected'}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border-dark px-4 py-3">
            <div className="flex items-center gap-3">
              <svg
                className="h-5 w-5 text-text-primary"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              <span className="text-sm font-medium text-text-primary">GitHub</span>
            </div>
            <span
              className={`text-xs font-medium ${
                user?.provider === 'github'
                  ? 'text-[var(--color-success)]'
                  : 'text-text-tertiary'
              }`}
            >
              {user?.provider === 'github' ? 'Connected' : 'Not connected'}
            </span>
          </div>
        </div>
      </div>

      {/* Account info */}
      <div className="mt-6 rounded-xl border border-border-dark bg-surface-1 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Account</h2>
        <div className="mt-4 space-y-3">
          {user?.created_at && (
            <div>
              <p className="text-sm font-medium text-text-primary">
                Account created
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                {new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          )}
        </div>
        <div className="mt-6 border-t border-border-dark pt-6">
          <button
            onClick={logout}
            className="rounded-lg border border-[rgba(248,113,113,0.3)] px-4 py-2 text-sm font-medium text-[var(--color-error)] transition-colors hover:bg-[rgba(248,113,113,0.12)]"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
